import React, { FC, ReactNode } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { selectDefaultRoute } from "Redux/Role/slice";
import ErrorFallback from "./ErrorBoundary";

// React keeps a failed lazy import, so only a reload fetches the page again.
const isChunkLoadError = (error: unknown) =>
  error instanceof Error && error.name === "ChunkLoadError";

const PageErrorFallback: FC<FallbackProps> = (props) => {
  const defaultRoute = useSelector(selectDefaultRoute);
  const reload = isChunkLoadError(props.error);
  return (
    <ErrorFallback
      {...props}
      resetErrorBoundary={
        reload ? () => window.location.reload() : props.resetErrorBoundary
      }
      retryLabel={reload ? "Reload page" : undefined}
      renderHome={(className) => (
        <Link className={className} to={defaultRoute}>
          Go to dashboard
        </Link>
      )}
    />
  );
};

type Props = {
  children?: ReactNode;
  /** Wraps the fallback, e.g. in the app shell. */
  renderFallback?: (fallback: ReactNode) => ReactNode;
};

/**
 * Catches a crash in one page so the rest of the app keeps working.
 * Going to another page clears the error.
 */
export const PageErrorBoundary: FC<Props> = ({
  children,
  renderFallback = (fallback) => fallback,
}) => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary
      resetKeys={[pathname]}
      fallbackRender={(props) =>
        renderFallback(<PageErrorFallback {...props} />)
      }
    >
      {children}
    </ErrorBoundary>
  );
};
